<template>
  <div class="clientDetail">
    <!-- 顶部返回 -->
    <div class="top-bar">
      <a @click="goBack" class="back-link">
        <a-icon type="arrow-left" /> 返回客户端列表
      </a>
    </div>

    <!-- 客户端头部 -->
    <div class="detail-header">
      <div class="header-main">
        <div class="header-title">
          <span class="client-name">{{ detail.clientName || '--' }}</span>
          <a-tag v-if="detail.clientCategory" color="orange" class="category-tag">{{
            getCategoryLabel(detail.clientCategory) }}</a-tag>
          <a-tag v-if="detail.clientType" :color="getTypeColor(detail.clientType)" class="type-tag"><a-icon
              :type="getTypeIcon(detail.clientType)" style="margin-right: 5px;" />{{ getTypeLabel(detail.clientType) }}</a-tag>
          <a-switch :checked="detail.status == 'ENABLED'" @change="handleStatusChange" :loading="statusLoading" />
          <span class="status-text"
            :class="{ 'enabled': detail.status == 'ENABLED', 'disabled': detail.status == 'DISABLED' }">
            {{ detail.status == 'ENABLED' ? '启用中' : '已停用' }}
          </span>
        </div>
        <div class="header-desc" v-if="detail.description">
          {{ detail.description }}
        </div>
        <div class="header-meta">
          <span class="meta-item">ClientId：<b>{{ detail.clientId || '--' }}</b></span>
          <span class="meta-item">归属租户：
            <b>{{ detail.tenantNames && detail.tenantNames.length ? detail.tenantNames.join('、') : '--' }}</b>
          </span>
          <span class="meta-item">创建人：<b>{{ detail.creator || '--' }}</b></span>
          <span class="meta-item">更新时间：<b>{{ detail.updateTime ? $moment(detail.updateTime).format('YYYY-MM-DD HH: mm: ss') : '--' }}</b></span>
        </div>
      </div>
      <div class="header-actions">
        <a-button icon="edit" @click="handleEdit">编辑</a-button>
        <a-button icon="delete" type="danger" ghost @click="handleDelete">删除客户端</a-button>
      </div>
    </div>

    <!-- Tab 内容 -->
    <div class="tab-panel">
      <a-tabs default-active-key="basic">
        <a-tab-pane key="basic">
          <span slot="tab">
            <a-icon type="info-circle" theme="filled" />
            基础信息
          </span>
          <BasicInfo :detail="detail" />
        </a-tab-pane>

        <a-tab-pane key="model">
          <span slot="tab">
            <a-icon type="code-sandbox-square" theme="filled" />
            型号管理
          </span>
          <ModelManagement :client-id="clientId" />
        </a-tab-pane>

        <!-- <a-tab-pane key="device">
          <span slot="tab">
            <a-icon type="unordered-list" />
            设备管理
          </span>
          <div class="placeholder-box">
            <a-icon type="database" class="placeholder-icon" />
            <div class="placeholder-text">设备管理功能开发中</div>
            <div class="placeholder-subtitle">后续可在此查看该客户端服务的设备列表</div>
          </div>
        </a-tab-pane> -->
      </a-tabs>
    </div>

    <!-- 编辑弹窗（复用 addClient） -->
    <add-client :visible="editVisible" :record="detail" @close="editVisible = false" @refresh="loadDetail" />

    <!-- 删除弹窗 -->
    <delete-client :visible="deleteVisible" :record="detail" @close="deleteVisible = false" @refresh="deletedBack" />
  </div>
</template>

<script>
import AddClient from './modal/addClient.vue'
import DeleteClient from './modal/deleteClient.vue'
import BasicInfo from './components/BasicInfo.vue'
import ModelManagement from './components/ModelManagement.vue'
import { CLIENT_TYPES, CLIENT_CATEGORIES } from './clientConstants.js'

export default {
  name: 'ClientDetail',
  components: { AddClient, DeleteClient, BasicInfo, ModelManagement },
  data() {
    return {
      clientId: '',
      detail: {},
      editVisible: false,
      deleteVisible: false,
      statusLoading: false,
    }
  },
  created() {
    this.clientId = this.$route.query.id || ''
    if (this.clientId) {
      this.loadDetail()
    } else {
      this.$message.warning('缺少客户端ID')
      this.goBack()
    }
  },
  methods: {
    /* 加载详情 */
    loadDetail() {
      this.$api.getClientDetail(this.clientId).then((res) => {
        if (res && res.code == 0) {
          this.detail = res.data
        }
      }).catch((err) => {
        console.error(err)
      })
    },

    /* 返回 */
    goBack() {
      this.$router.push({ name: 'ClientList' })
    },

    /* 编辑 */
    handleEdit() {
      this.editVisible = true
    },

    /* 删除 */
    handleDelete() {
      this.deleteVisible = true
    },

    /* 删除后返回 */
    deletedBack() {
      this.$router.push({ name: 'ClientList' })
    },

    /* 切换状态 */
    handleStatusChange(checked) {
      this.statusLoading = true

      this.$confirm({
        title: '操作确认',
        content: checked ? '确定启用该客户端？' : '停用后其下所有设备将停止接入，确认停用？',
        okText: '确定',
        cancelText: '取消',
        okType: checked ? 'primary' : 'danger',
        icon: 'warning',
        onOk: () => {
          const newStatus = checked ? 'ENABLED' : 'DISABLED'
          this.$api.changeClientStatus(this.clientId, { status: newStatus }).then((res) => {
            this.statusLoading = false
            if (res && res.code == 0) {
              this.detail.status = newStatus
              this.loadDetail()
              this.$message.success(checked ? '已启用' : '已停用')
            } else {
              this.$message.error('操作失败')
            }
          }).catch(() => {
            this.statusLoading = false
          })
        },
        onCancel: () => {
          this.statusLoading = false
        },
      })
    },

    /* 工具方法 */
    getTypeLabel(value) {
      const t = CLIENT_TYPES.find((i) => i.value === value)
      return t ? t.label : value
    },
    getTypeIcon(value) {
      const t = CLIENT_TYPES.find((i) => i.value === value)
      return t ? t.icon : 'user'
    },
    getTypeColor(value) {
      const t = CLIENT_TYPES.find((i) => i.value === value)
      return t ? t.color : 'blue'
    },
    getCategoryLabel(value) {
      const t = CLIENT_CATEGORIES.find((i) => i.value === value)
      return t ? t.label : value
    },
  },
}
</script>

<style lang="less" scoped>
@import '@/assets/CSS/global.less';

.clientDetail {
  background-color: #fff;
  height: calc(100vh - 120px);
  padding: 16px;
  border-radius: 4px;
  overflow-y: auto;
}

.top-bar {
  margin-bottom: 12px;

  .back-link {
    color: #1890ff;
    font-size: 14px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}

.detail-header {
  border-bottom: 1px solid #f0f0f0;
  padding: 0 20px 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  // background: #fafafa;

  .header-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;

    .client-name {
      font-size: 22px;
      font-weight: 600;
      color: #151515;
    }

    .status-text {
      font-size: 14px;
      color: #666;
      margin-left: 4px;
      display: inline-flex;
      align-items: center;
      gap: 6px;

      &.enabled {
        &::before {
          content: '';
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #52c41a;
          box-shadow: 0 0 0 2px rgba(82, 196, 26, .12);
        }
      }

      &.disabled {
        &::before {
          content: '';
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #f5222d;
          box-shadow: 0 0 0 2px rgba(245, 34, 45, .12);
        }
      }
    }
  }

  .header-desc {
    font-size: 13px;
    color: #666;
    margin-bottom: 10px;
    max-width: 800px;
    line-height: 1.5;
  }

  .header-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    font-size: 12px;
    color: #999;

    b {
      color: #000000bf;
      margin-right: 4px;
    }
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }
}

.tab-panel {
  background: #fff;
  padding: 4px 0;
}

.placeholder-box {
  text-align: center;
  padding: 80px 20px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px dashed #e8e8e8;
  margin-top: 20px;

  .placeholder-icon {
    font-size: 56px;
    color: #bfbfbf;
    margin-bottom: 16px;
  }

  .placeholder-text {
    font-size: 16px;
    font-weight: 500;
    color: #595959;
    margin-bottom: 8px;
  }

  .placeholder-subtitle {
    font-size: 13px;
    color: #999;
  }
}
</style>
